import React from 'react'
import { AsyncState, fromJSX, nothing, Updater, Widget } from 'widgets-for-react'
import { LinkLayout } from '../../../../../Shared/Frontend/utils/links'
import { pushDatalayer } from '../../../seo/datalayer'
import { CMSLoaderState, PageData } from '../../cmsPage.state'
import { State } from '../../cmsPage.widget'
import { SharedBlockLayout } from '../../modernBlocks/blocks/shared/sharedBlock.layout'
import { LinkGridBlockLayout } from '../../modernBlocks/blocks/gridBlock/gridBlock.layout'
import { convertTitleForBackground, unEntity } from '../../utils/html'

// ----- Default

const PageHeaderLayout = (currentPageData: PageData, state: State): Widget<Updater<State>> => {
  if ((currentPageData.header.kind != "normal" && currentPageData.header.kind != "fullscreen")) return nothing()
  const { image, imageSource, imageSize, linkPrimary, linkSecondary, pageIntro, pageTitle, publishDate } = currentPageData.header

  const pageKind = currentPageData.page.kind
  const firstBlockIsDonation = state.rootModernBlocksState.modernBlocks.blocks.get(0)?.kind == 'donationFormBlock'
  if (pageKind == "donatie_pagina" && firstBlockIsDonation) return nothing()

  return fromJSX((setState) => (
    <>
      {imageSize == 'large'
        ? <>
            <header className="pageheader ph--image-full">
              <div className='ph__inner ls-container--l'>
                <div className='ph__content'>
                  <div className='ph__content-inner'>
                    {pageTitle && (
                      <h1 className='ph__title title--l title--bg' dangerouslySetInnerHTML={{ __html: unEntity(convertTitleForBackground(pageTitle)) }} />
                    )}
                  </div>
                </div>

                <div className="ph__visual ls-inner-container--m">
                  <div className='ph__visual-inner fit-picture'>
                    {image && imageSource &&
                      <SharedBlockLayout.ModernImagePicture
                        pictureKey='ph'
                        image={image}
                        sources={imageSource}
                        lazyload={false}
                        priority='high'
                        classNames="ph__image"
                        pictureClassNames='ph__picture'
                        imageProportions={{
                          width: '640',
                          height: '500',
                          sizes: '100vw'
                        }}
                      />
                    }
                  </div>
                </div>
              </div>
            </header>
          </>
        : <>
          <header className={`pageheader ph--${image ? 'image' : 'text'} ls-container--l ls-gutter`}>
            <div className='ph__inner ls-container--l'>
              <div className='ph__content'>
                <div className='ph__content-inner'>
                  {publishDate && (
                    <time dateTime={publishDate.toISOString()} className='ph__meta text--s'>{publishDate.toLocaleString("nl-NL", {day: 'numeric', month: 'long', year: 'numeric'})}</time>
                  )}

                  {pageTitle && (
                    <h1
                      className={`ph__title title--l ${image ? 'title--bg title--left' : ''}`}
                      dangerouslySetInnerHTML={{ __html: image ? unEntity(convertTitleForBackground(pageTitle)) : unEntity(pageTitle) }}
                    />
                  )}

                  {pageIntro ? (
                    typeof pageIntro == 'string' ? (
                      <p className='ph__intro' dangerouslySetInnerHTML={{ __html: pageIntro }} />
                    ) : (
                      <p className='ph__intro'>{pageIntro}</p>
                    )
                  ) : (
                    <></>
                  )}

                  {(linkPrimary || linkSecondary) ? (
                    <div className='ph__actions ls-clickables'>
                      {linkPrimary && (
                        <div className="ph__action ls-clickable">
                          <LinkLayout
                            link={linkPrimary}
                            className='ph__button button button--primary button--on-darkred'
                            onClick={() => {
                              pushDatalayer({
                                event: 'event.gtm',
                                category: 'pageheader-cta',
                                action: 'primary-' + linkPrimary.title,
                                label: window.location.href
                              })
                            }}
                          >
                            <span className='button__label'>{linkPrimary.title}</span>
                          </LinkLayout>
                        </div>
                      )}
                      {linkSecondary && (
                        <div className="ph__action ls-clickable">
                          <LinkLayout
                            link={linkSecondary}
                            className='ph__link button button--secondary button--on-darkred'
                            onClick={() => {
                              pushDatalayer({
                                event: 'event.gtm',
                                category: 'pageheader-cta',
                                action: 'secondary-' + linkSecondary.title,
                                label: window.location.href
                              })
                            }}
                          >
                            <span className="button__label">{linkSecondary.title}</span>
                          </LinkLayout>
                        </div>
                      )}
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              </div>

              {image && <div className="ph__visual">
                <div className='ph__visual-inner fit-picture'>
                  <SharedBlockLayout.ModernImageSrcSet
                    image={image}
                    lazyload={false}
                    priority='high'
                    classNames="ph__image"
                    imageProportions={{
                      width: '590',
                      height: '500',
                      sizes: '(min-width: 768px) 37vw, 590px'
                    }}
                  />
                </div>
                <svg className="ph__mask" fill="none" width="745" height="631" viewBox="0 0 745 631"><path clipRule="evenodd" d="m745 0h-745v631h745v-84.873c-6.832.001-13.667.034-20.502.101-197.657-.263-394.338 27.706-584.088 83.064l-138.41-527.465c233.147-66.6174 474.49-100.17111 716.962-99.67832h6.919c6.374-.0731 12.747-.12276 19.119-.14903z" fill="#6E273D" fillRule="evenodd"/></svg>
              </div>}
            </div>
          </header>
        </>
      }
    </>
  ))
}


const HomeHeaderLayout = (currentPageData: PageData, state: State): Widget<Updater<State>> => {
  if (currentPageData.header.kind != "home") return nothing()

  const { image, linkPrimary, linkSecondary, pageIntro, pageTitle, grid } = currentPageData.header
  return fromJSX(setState => {
    return <>
      <header className='homeheader hh'>
        <div className='hh__outer ls-container--xl-nogut'>
          <div className="hh__inner ls-container--l">
            <div className='hh__content'>
              <div className='hh__content-inner'>
                {pageTitle && (
                  <h1 className='hh__title title--l title--bg' dangerouslySetInnerHTML={{ __html: unEntity(convertTitleForBackground(pageTitle)) }} />
                )}

                {pageIntro ? (
                  typeof pageIntro == 'string' ? (
                    <p className='hh__intro' dangerouslySetInnerHTML={{ __html: pageIntro }} />
                  ) : (
                    <p className='hh__intro'>{pageIntro}</p>
                  )
                ) : (
                  <></>
                )}

                {(linkPrimary || linkSecondary) ? (
                  <div className='hh__actions ls-clickables'>
                    {linkPrimary && (
                      <div className="hh__action ls-clickable">
                        <LinkLayout
                          link={linkPrimary}
                          className='hh__button button button--primary button--on-darkred'
                          onClick={() => {
                            pushDatalayer({
                              event: 'event.gtm',
                              category: 'pageheader-cta',
                              action: 'primary-' + linkPrimary.title,
                              label: window.location.href
                            })
                          }}
                        >
                          <span className='button__label'>{linkPrimary.title}</span>
                        </LinkLayout>
                      </div>
                    )}
                    {linkSecondary && (
                      <div className="hh__action ls-clickable">
                        <LinkLayout
                          link={linkSecondary}
                          className='hh__link button button--secondary button--on-darkred'
                          onClick={() => {
                            pushDatalayer({
                              event: 'event.gtm',
                              category: 'pageheader-cta',
                              action: 'secondary-' + linkSecondary.title,
                              label: window.location.href
                            })
                          }}
                        >
                          <span className="button__label">{linkSecondary.title}</span>
                        </LinkLayout>
                      </div>
                    )}
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>

            <div className="hh__visual">
              <div className='hh__visual-inner fit-picture'>
                <SharedBlockLayout.ModernImageSrcSet
                  image={image}
                  lazyload={false}
                  priority='high'
                  classNames="hh__image"
                  imageProportions={{
                    width: '882',
                    height: '746',
                    sizes: '(min-width: 1440px) 878px, (min-width: 768px) 743px, 441px'
                  }}
                />
              </div>
              <svg className="hh__swoosh" width="402" height="39" viewBox="0 0 402 39" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M0 38.8088H402V9.6139C354.652 2.79447 306.732 -0.423264 258.65 0.0445583C171.333 -0.0716241 84.4474 12.2843 0.623326 36.7387L0 38.8088Z" fill="#6E273D"/></svg>
            </div>
          </div>
        </div>

        {grid && (
          <section className="block block--grid block--grid-image">
            <LinkGridBlockLayout.Content
              items={grid}
              showPrimary={false}
              showSecondary={false}
            />
          </section>
        )}
      </header>
    </>
  })
}

export const renderPageHeader = (state: State, CMSLoaderState: AsyncState<CMSLoaderState>): Widget<Updater<State>> => {
  if (state.isCustomPage != 'none' && state[state.isCustomPage].header != undefined) {
    return state[state.isCustomPage].header!(state)
  }

  if (CMSLoaderState.kind != 'loaded') return nothing()

  const { pageData: currentPageData } = CMSLoaderState.value
  const { kind } = currentPageData.header

  switch (kind) {
    case 'home':
      return HomeHeaderLayout(currentPageData, state)
    case 'normal':
    case 'fullscreen':
      return PageHeaderLayout(currentPageData, state)
    default:
      return fromJSX((_) => <p>Not supported header</p>)
  }
}
